import {
  BULK_FILTER_VALUE_SEPARATOR,
  COMPANY_ID_FILTER_SEPARATOR,
  DURATION_FILTER_VALUE_SEPARATOR,
  RANGE_FILTER_SEPARATOR,
} from '@aibees/elephant/constants/filters';
import {
  SearchFilter,
  SearchFilterOption,
  SearchFilterType,
} from '@aibees/elephant/feature/search-filters/types';
import { CompanyDepartmentKeys } from '@aibees/elephant/feature/search-table-company';
import { DEPARTMENTS_TRANSLATIONS } from '@aibees/elephant/hooks/metrics/utils';
import {
  CompanySocialMedia,
  SearchBrowseParams,
  SearchTable,
} from '@aibees/elephant/types';
import { Label } from '@aibees/elephant/types/label';
import { Maybe } from '@aibees/shared/definition';
import {
  capitalize,
  fDate,
  removePathFramUrl,
  toNumber,
} from '@aibees/shared/utils';
import { getDomain } from 'tldts';
import {
  CompanyFundingType,
  humanReadableFundingType,
} from '../../config/funding-types';
import { JOB_FUNCTION_TRANSLATION } from '../../config/job-functions';
import { INNER_SEPARATOR } from '../../filters/employee-by-department-filter/config';
import type {
  FieldData,
  FilterBaseTagData,
  FilterItemTag,
  FilterTagAlternate,
} from './types';
import { PersonProfileBadge } from '@aibees/elephant/types/person';

interface SearchFilterWithGroup extends SearchFilter {
  group?: string;
}

export function getTags(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  return getFilterTagsAlternate(params, filter);
}

export function getFilterTagsAlternate(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  return extractFilterTags(params, filter);
}

export function extractFilterTags(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
): FilterTagAlternate {
  const fieldTags = filter.utils?.tags?.getFilterItemTagFunc
    ? filter.utils?.tags.getFilterItemTagFunc(params, filter)
    : getFieldTags(params, filter, filter.field!);

  const tags =
    fieldTags?.data && !fieldTags.hide ? [fieldTags] : ([] as FilterItemTag[]);

  let hasContent = tags.length > 0;
  let items: FilterTagAlternate[] | undefined;

  if (filter.items) {
    items = [];
    for (let i = 0; i < filter.items.length; i++) {
      const child = filter.items[i];
      if (child.type === SearchFilterType.APPLIED_FILTERS) continue;
      const childTags = extractFilterTags(params, child);
      items.push(childTags);
      if (!hasContent && !childTags.empty) hasContent = true;
    }
  }

  return {
    name: filter.name,
    tags,
    items,
    empty: !hasContent,
    root: filter.type === SearchFilterType.ROOT,
    mainItem: filter.mainItem,
    icon: filter.icon,
    hidden: filter.hidden,
    showAsCard: filter.utils?.tags?.config?.showAsCard,
    layer: filter.layer,
    type: filter.type,
    tagsCardTitle: filter.utils?.tags?.config?.tagsCardTitle,
  };
}

function getFilterTags(
  searchTable: Maybe<SearchTable>,
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  const items = getFilterItems(searchTable, filter);
  const grouped: Record<string, FilterItemTag[]> = {};

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const fieldTags = item.utils?.tags?.getFilterItemTagFunc
      ? item.utils?.tags.getFilterItemTagFunc(params, item)
      : getFieldTags(params, item, item.field!);
    if (fieldTags?.data) {
      const key = fieldTags.group || fieldTags.name;
      const arr = grouped[key];
      if (arr) arr.push(fieldTags);
      else grouped[key] = [fieldTags];
    }
  }

  return {
    name: filter.name,
    field: filter.field,
    hide: filter.metadata?.hideTags,
    items: grouped,
  } as FilterBaseTagData;
}

export function getFilteringFields(
  searchTable: Maybe<SearchTable>,
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  const tags = getFilterTags(searchTable, params, filter);
  const result: string[] = [];
  const grouped = tags.items;
  const keys = Object.keys(grouped);
  for (let ki = 0; ki < keys.length; ki++) {
    const arr = grouped[keys[ki]];
    for (let ti = 0; ti < arr.length; ti++) result.push(arr[ti].field);
  }
  return result;
}

export function getFilterItems(
  searchTable: Maybe<SearchTable>,
  filter: SearchFilter,
  group?: string
) {
  const result: SearchFilterWithGroup[] = [];
  collectFilterItems(searchTable, filter, group, result);
  return result;
}

function collectFilterItems(
  searchTable: Maybe<SearchTable>,
  filter: SearchFilter,
  group: string | undefined,
  out: SearchFilterWithGroup[]
): void {
  if (filter.field) {
    if (group) {
      const draft = { ...filter, group } as SearchFilterWithGroup;
      out.push(draft);
    } else {
      out.push(filter as SearchFilterWithGroup);
    }
  }
  const { items } = filter;
  if (items) {
    const childGroup =
      filter.type !== SearchFilterType.ROOT ? filter.name : undefined;
    for (let i = 0; i < items.length; i++) {
      collectFilterItems(searchTable, items[i], childGroup, out);
    }
  }
}

export function getFilterFields(filter: SearchFilter) {
  const result: string[] = [];
  collectFilterFields(filter, result);
  return result;
}

function collectFilterFields(filter: SearchFilter, out: string[]): void {
  if (filter.field) out.push(filter.field);
  const { items } = filter;
  if (items) {
    for (let i = 0; i < items.length; i++) collectFilterFields(items[i], out);
  }
}

function getFieldTags(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilterWithGroup,
  field: string
) {
  const data: FilterItemTag = {
    data: getFieldTagDataByType(params, filter),
    name: filter.name,
    color: filter.metadata?.color,
    icon: filter.metadata?.icon,
    type: filter.type,
    group: filter.group,
    field,
    utils: filter.utils,
    hide: filter.metadata?.hideTags,
  };
  return data.data?.length ? data : undefined;
}

function getFieldTagDataByType(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  if (filter.field) {
    if (filter.type === SearchFilterType.DURATION)
      return getDurationFieldTagData(params, filter);
    return getFieldTagData(params, filter);
  }
}

function getFieldTagData(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  if (filter.type === SearchFilterType.BULK)
    return getFieldTagDataBulk(params, filter);
  if (
    filter.type === SearchFilterType.NUMBER_RANGE ||
    filter.type === SearchFilterType.DATE_RANGE
  )
    return getFieldTagDataNumberRange(params, filter);
  const raw = params[filter.field!];
  if (!raw) return undefined;
  const parts = raw.split(getParamsDelimiter(filter.type));
  const result: FieldData[] = [];
  for (let i = 0; i < parts.length; i++) {
    const x = parts[i];
    const label = getLabel(filter, x, i);
    if (x && label) result.push({ label, value: x });
  }
  return result.length ? result : undefined;
}

function getFieldTagDataNumberRange(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
): FieldData[] {
  const value = params[filter.field!];
  if (!value) return [];
  const delimiter = filter.metadata?.delimiter ?? RANGE_FILTER_SEPARATOR;
  const rawRange = value.split(delimiter);
  const isDateRange = filter.type === SearchFilterType.DATE_RANGE;
  const range = isDateRange
    ? rawRange.map((x) => fDate(toNumber(x) ?? 0 / 1000))
    : rawRange;

  if (range.length > 1) {
    const hasMin = !!range[0]?.length;
    const hasMax = !!range[1]?.length;

    if (hasMin && hasMax) {
      if (range[0] === '0' && range[1] === '0') {
        return [{ label: 'N/A', value: '0-0' }];
      }
      if (range[0] === range[1]) {
        return [{ label: `In Total ${range[0]}`, value }];
      }
      const symbols = getLabelSymbol(filter);
      return [
        { label: `${range[0]} ${symbols?.[0]}`, value: range[0] },
        { label: `${range[1]} ${symbols?.[1]}`, value: range[1] },
      ];
    }
    if (hasMax) return [{ label: `Up to ${range[1]}`, value: range[1] }];
    if (hasMin) return [{ label: `${range[0]} or Above`, value: range[0] }];
    return [];
  }

  return range[0] ? [{ label: `${range[0]} or Above`, value: range[0] }] : [];
}

function getFieldTagDataBulk(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  const val = params[filter.field!];
  return val ? [{ label: val, value: val }] : undefined;
}

export function getParamsDelimiter(type?: SearchFilterType) {
  if (
    type === SearchFilterType.DATE_RANGE ||
    type === SearchFilterType.NUMBER_RANGE ||
    type === SearchFilterType.DURATION
  )
    return RANGE_FILTER_SEPARATOR;
  if (type === SearchFilterType.EMPLOYEE_BY_DEPARTMENT) return ';';
  return '^';
}

export function getFieldsByFilterItems(items: SearchFilter[]): string[] {
  const result: string[] = [];
  for (let i = 0; i < items.length; i++) collectFilterFields(items[i], result);
  return result;
}

export function getTagsCount(data: FilterTagAlternate) {
  return countTagsData(data);
}

function countTagsData(data: FilterTagAlternate): number {
  let count = 0;
  const { tags } = data;
  if (tags) {
    for (let i = 0; i < tags.length; i++) {
      const d = tags[i].data;
      if (d) count += d.length;
    }
  }
  const { items } = data;
  if (items) {
    for (let i = 0; i < items.length; i++) count += countTagsData(items[i]);
  }
  return count;
}

export function getFilterTagsFlattened(data: FilterTagAlternate) {
  const result: FilterItemTag[] = [];
  collectFilterTags(data, result);
  return result;
}

function collectFilterTags(
  data: FilterTagAlternate,
  out: FilterItemTag[]
): void {
  const { tags } = data;
  if (tags) {
    for (let i = 0; i < tags.length; i++) out.push(tags[i]);
  }
  const { items } = data;
  if (items) {
    for (let i = 0; i < items.length; i++) collectFilterTags(items[i], out);
  }
}

// explanation: this function is [just one level deep] flattening the items of the filter tag alternate
export function flattenFilterTagItems(data: FilterTagAlternate) {
  const dataItems = data.items;
  if (!dataItems || dataItems.length === 0) return { ...data, items: [] };
  const items: FilterTagAlternate[] = [];
  for (let i = 0; i < dataItems.length; i++) {
    const childItems = dataItems[i].items;
    if (childItems) {
      for (let j = 0; j < childItems.length; j++) items.push(childItems[j]);
    } else {
      items.push(dataItems[i]);
    }
  }
  return { ...data, items };
}

// Location Filter Utils Start

export function getButtonGroupFilterTags(
  searchTable: Maybe<SearchTable>,
  params: SearchBrowseParams,
  filter: SearchFilter
) {
  const grouped: Record<string, FilterItemTag[]> = {};

  const filterItems = filter.items;
  if (filterItems) {
    for (let xi = 0; xi < filterItems.length; xi++) {
      const x = filterItems[xi];
      if (x.type === SearchFilterType.APPLIED_FILTERS) continue;
      const childItems = x.items;
      if (!childItems) continue;
      for (let yi = 0; yi < childItems.length; yi++) {
        const y = childItems[yi];
        const items = getFilterItems(searchTable, y);
        for (let ii = 0; ii < items.length; ii++) {
          const fieldTags = getFieldTags(params, items[ii], items[ii].field!);
          if (fieldTags?.data?.length) {
            fieldTags.group = y.name;
            const key = fieldTags.group || fieldTags.name;
            const arr = grouped[key];
            if (arr) arr.push(fieldTags);
            else grouped[key] = [fieldTags];
          }
        }
      }
    }
  }

  return {
    name: filter.name,
    field: filter.field,
    hide: filter.metadata?.hideTags,
    items: grouped,
  } as FilterBaseTagData;
}

// Location Filter Utils End

// Duration Filter Utils Start

export function getDurationFilterFieldTags(
  params: SearchBrowseParams,
  filter: SearchFilterWithGroup
) {
  return {
    data: getDurationFieldTagData(params, filter),
    name: filter.name,
    color: filter.metadata?.color,
    icon: filter.metadata?.icon,
    type: filter.type,
    group: filter.group || undefined,
    field: filter.field ?? '',
    utils: filter.utils,
    hide: filter.metadata?.hideTags,
  } as FilterItemTag;
}

function getDurationFieldTagData(
  params: Partial<SearchBrowseParams>,
  filter: SearchFilter
) {
  const raw = params[filter.field!];
  if (!raw) return undefined;
  const parts = raw.split(getParamsDelimiter(filter.type));
  const result: FieldData[] = [];

  for (let i = 0; i < parts.length; i++) {
    const x = parts[i];
    const arr = x.split(DURATION_FILTER_VALUE_SEPARATOR);
    const years = arr[0];
    const months = arr[1];
    if (!years && !months) continue;
    const v = years
      ? months
        ? `${years} Years,${months} Months`
        : `${years} Years`
      : `${months} Months`;
    result.push({ label: getLabel(filter, v, i), value: x });
  }

  return result.length ? result : undefined;
}

// Duration Filter Utils End

// Label Utils Start

function getLabelSymbol(filter: SearchFilter) {
  if (
    filter.type === SearchFilterType.DATE_RANGE ||
    filter.type === SearchFilterType.NUMBER_RANGE
  )
    return ['<', '>'];
  if (filter.type === SearchFilterType.DURATION) return ['Min:', 'Max:'];
  if (filter.metadata?.delimiter) return filter.metadata?.delimiter;
  return undefined;
}

function getLabel(filter: SearchFilter, value: string, index: number) {
  const symbols = getLabelSymbol(filter);
  return filter.utils?.tags?.labelFormatter
    ? filter.utils?.tags?.labelFormatter(value, value, {
        symbol: symbols ? symbols[index] : undefined,
        delimiter: filter.metadata?.delimiter,
      })
    : value;
}

export function sicCodeLabelParser(
  label: string,
  value: string,
  options: {
    symbol?: string;
    delimiter?: string;
    metadata?: Record<string, string>;
  }
) {
  return options?.metadata?.description ?? value;
  // const splitted = label?.split(options.delimiter!);
  // return splitted.join(' ');
}

export function durationLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return `${options.symbol} ${value}`;
}

export function employeeByDepartmentRangeLabelParser(value: string) {
  const parts = value.split(INNER_SEPARATOR);
  const rangeStr = parts[1];
  if (!rangeStr) return '';
  const range = rangeStr.split(RANGE_FILTER_SEPARATOR);
  const min = range[0];
  const max = range[1];

  if (range.length > 1) {
    if (min && max) {
      return min === max ? `In Total ${min}` : `Between ${min} and ${max}`;
    }
    if (max) return `Between 0 and ${max}`;
    if (min) return `${min} or Above`;
    return '';
  }

  return min ? `${min} or Above` : '';
}

export function headcountGrowthRangeLabelParser(value: string) {
  const parts = value.split(INNER_SEPARATOR);
  const rangeStr = parts[1];
  if (!rangeStr) return '';
  const range = rangeStr.split(RANGE_FILTER_SEPARATOR);
  const min = range[0];
  const max = range[1];

  if (range.length > 1) {
    if (min && max) {
      return min === max ? `In Total ${min}%` : `Between ${min}% and ${max}%`;
    }
    if (max)
      return Number(max) <= 0 ? `Up to ${max}%` : `Between 0% and ${max}%`;
    if (min) return `${min}% or Above`;
    return '';
  }

  return min ? `${min}% or Above` : '';
}

export function zipCodeLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options?.delimiter)[0] : label;
}

export function departmentLabelFormatter(
  label: string,
  value: CompanyDepartmentKeys
) {
  return DEPARTMENTS_TRANSLATIONS[value as CompanyDepartmentKeys] ?? label;
}

export function jobRoleDepartmentLabelFormatter(
  label: string,
  value: string,
  options: { delimiter?: string }
) {
  if (options.delimiter && value.includes(options.delimiter)) {
    const parts = value.split(options.delimiter);
    const department = parts[0];
    const subDepartment = parts[1];
    return subDepartment
      ? `${JOB_FUNCTION_TRANSLATION[subDepartment]} / ${JOB_FUNCTION_TRANSLATION[department]}`
      : JOB_FUNCTION_TRANSLATION[department];
  }
  return JOB_FUNCTION_TRANSLATION[value];
}

export function listFilterLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function recordOwnershipFilterLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function companyTypeFilterLabelParser(
  label: Maybe<string>,
  value: Maybe<string>
) {
  return capitalize((value ?? label ?? '').replace('_', ' '));
}

export function FundingTypeLabelParser(label: string, value: string) {
  return humanReadableFundingType((label ?? value) as CompanyFundingType);
}

// Label Utils End

// Draft Utils Start

export function buildClearAllDraft(data: FilterTagAlternate) {
  if (!data) return {};
  const draft: Record<string, undefined> = {};
  collectFieldsIntoDraft(data, draft);
  return draft;
}

function collectFieldsIntoDraft(
  data: FilterTagAlternate,
  draft: Record<string, undefined>
): void {
  const { tags } = data;
  if (tags) {
    for (let i = 0; i < tags.length; i++) draft[tags[i].field] = undefined;
  }
  const { items } = data;
  if (items) {
    for (let i = 0; i < items.length; i++)
      collectFieldsIntoDraft(items[i], draft);
  }
}

export function buildClearItemDraft(tags?: FilterItemTag[]) {
  if (!tags) return {};
  const draft: Record<string, undefined> = {};
  for (let i = 0; i < tags.length; i++) draft[tags[i].field] = undefined;
  return draft;
}

// Draft Utils End

// Search in filter configs

export function pathToFilterArray(
  array: SearchFilter[],
  targetKey: keyof SearchFilter,
  targetValue: string,
  returnKey: keyof SearchFilter
) {
  const stringResult = pathToFilter(array, targetKey, targetValue, returnKey);
  return stringResult.split('.');
}

function pathToFilter(
  array: SearchFilter[],
  targetKey: keyof SearchFilter,
  targetValue: string,
  returnKey: keyof SearchFilter
): string {
  for (let i = 0; i < array.length; i++) {
    const item = array[i];
    if (item[targetKey] === targetValue) return String(item[returnKey]);
    const children = item.items;
    if (children) {
      const tmp = pathToFilter(children, targetKey, targetValue, returnKey);
      if (tmp) return `${String(item[returnKey])}.${tmp}`;
    }
  }
  return '';
}

// Value Formatters Utils Start

export function zipCodeValueFormatter(
  v: { lat: string; lng: string },
  delimiter: string,
  item: {
    name: string;
    location: { lat: string; lng: string };
  }
) {
  return `${item.name}${delimiter}${item.location.lat}${delimiter}${item.location.lng}`;
}

export function recordOwnershipFilterValueFormatter(
  value: string,
  delimiter: string,
  item: Label
) {
  return `${item.name}${delimiter}${value}`;
}

// Value Formatters Utils End

export function getLevel(data?: FilterTagAlternate, currentLevel?: number) {
  return data?.name?.length ? (currentLevel ?? 0) + 1 : currentLevel ?? 0;
}

export function showSubtitle(data?: FilterTagAlternate) {
  return !!data?.name?.length;
}

export function showTags(data?: FilterTagAlternate) {
  return !data?.root && !!data?.tags?.length;
}

export function getTagsParams(tags: FilterTagAlternate) {
  const items = tags?.items;
  if (!items || items.length === 0) return [];
  const result: FilterItemTag[] = [];
  for (let i = 0; i < items.length; i++) {
    const itemTags = items[i].tags;
    if (itemTags) {
      for (let j = 0; j < itemTags.length; j++) result.push(itemTags[j]);
    }
  }
  return result;
}

export function getDomainIfExist(value: string) {
  return getDomain(value) ?? value;
}

export function companySearchLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function peopleSearchLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function educationSearchLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function dataRequestValueFormatter(
  value: string,
  delimiter: string,
  item: { name: string; records: number }
) {
  return `${item.name}${delimiter}${value}`;
}

export function dataRequestParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function enrichmentRequestValueFormatter(
  value: string,
  delimiter: string,
  item: { name: string; records: number }
) {
  return `${item.name}${delimiter}${value}`;
}

export function enrichmentRequestParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  return options?.delimiter ? value.split(options.delimiter)[0] : label;
}

export function getLocationFilterFieldTags(
  params: SearchBrowseParams,
  filter: SearchFilterWithGroup
) {
  const raw = params[filter.field!];
  const cleanData = raw
    ? raw.split(getParamsDelimiter(filter.type)).map((i) => {
        const parts = i.split('::');
        const country = parts[1];
        const state = parts[2];
        return {
          label: state ? `${state} / ${country}` : country,
          value: i,
        };
      })
    : undefined;

  return {
    data: cleanData,
    name: filter.name,
    color: filter.metadata?.color,
    icon: filter.metadata?.icon,
    type: filter.type,
    group: filter.group || undefined,
    field: filter.field ?? '',
    utils: filter.utils,
    hide: filter.metadata?.hideTags,
  } as FilterItemTag;
}

export function technologiesLabelFormatter(
  label: string,
  value: string,
  options: { delimiter?: string }
) {
  if (options.delimiter && value.includes(options.delimiter)) {
    const parts = value.split(options.delimiter);
    return `${parts[1]} / ${parts[0]}`;
  }
  return value;
}

export function locationsLabelFormatter(
  label: string,
  value: string,
  options: { delimiter?: string }
) {
  if (options.delimiter && value.includes(options.delimiter)) {
    const parts = value.split(options.delimiter);
    return parts[2] ? `${parts[2]} / ${parts[1]}` : parts[1];
  }
  return value;
}

export function jobChangeTagLabelParser(label: string, value: string) {
  return value === 'true' ? 'Yes' : 'No';
}

export function generateEducationFilterYearOptions() {
  const currentYear = new Date().getFullYear();
  const startYear = 1925;
  const count = currentYear - startYear + 1;
  const years: SearchFilterOption[] = new Array(count);
  for (let i = 0; i < count; i++) {
    const yr = String(currentYear - i);
    years[i] = { value: yr, label: yr, type: 'STRING' } as SearchFilterOption;
  }
  return years;
}

export function companySocialMediaFilterLabelParser(
  label: Maybe<string>,
  value: Maybe<string>
) {
  return capitalize(
    (value ?? label ?? '').replace(CompanySocialMedia.TWITTER, 'X (Twitter)')
  );
}

export function personProfileBadgeFilterLabelParser(
  label: Maybe<string>,
  value: Maybe<PersonProfileBadge>
) {
  switch (value) {
    case PersonProfileBadge.VERIFIED:
      return 'Verified Account';
    case PersonProfileBadge.PREMIUM:
      return 'Paid Social Members';
    case PersonProfileBadge.OPEN_TO_WORK:
      return 'Open to Work';
    case PersonProfileBadge.INFLUENCER:
      return 'Influencer';
    case PersonProfileBadge.CREATOR:
      return 'Creator';
    case PersonProfileBadge.HIRING:
      return 'Hiring (Beta)';
    default:
      return capitalize((value ?? label ?? '').replace('_', ' '));
  }
}

export function companyDomainValueFormatter(
  value: string,
  delimiter: string,
  item: { name: string; records: number }
) {
  return removePathFramUrl(decodeURIComponent(value ?? ''));
}

export function linkedinUrlValueFormatter(
  value: string,
  delimiter: string,
  item: { name: string; records: number }
) {
  return decodeURIComponent(value ?? '');
}

export function companySearchIdsLabelParser(
  label: string,
  value: string,
  options: { symbol?: string; delimiter?: string }
) {
  const idsLength = value.split(
    options.delimiter ?? COMPANY_ID_FILTER_SEPARATOR
  )[1];
  return `Selected: ${idsLength}`;
}

export function getBulkSearchFilterFieldTags(
  params: SearchBrowseParams,
  filter: SearchFilterWithGroup
) {
  const value = params[filter.field!];
  let data: FieldData[] | undefined;
  if (value) {
    const parts = value.split(BULK_FILTER_VALUE_SEPARATOR);
    const count = parts[1];
    const label = parts[2];
    data = [
      {
        label: `${label}${Number(count) > 4 ? ', ...' : ''}`,
        value,
      },
    ];
  }

  return {
    data,
    name: filter.name,
    color: filter.metadata?.color,
    icon: filter.metadata?.icon,
    type: filter.type,
    group: filter.group || undefined,
    field: filter.field ?? '',
    utils: filter.utils,
    hide: filter.metadata?.hideTags,
  } as FilterItemTag;
}
